import AppBackButton from '@nxsys/components/AppBackButton';
import AppCard from '@nxsys/components/AppCard';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetBankFilesReportParams } from '@nxsys/store/slices/report/types';
import { getColumns } from './columns';
import { reportActions } from '@nxsys/store/slices/report/reducer/reportSlice';
import { BankFilesType } from '@nxsys/types/models/bankFilesReports/bankfiles';
import * as XLSX from 'xlsx';
import { AppConfirmation } from '@nxsys/components/AppConfirmationModal';
import GeneratePaymentFileModal, {
  GeneratePaymentFileValues,
} from '../components/GeneratePaymentFileModal';
import { useGeneratePaymentFile } from '@nxsys/hooks/api/report';
import { toNumber } from 'lodash';
import { showMessage } from '@nxsys/utils/message';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import { downloadXMLFile } from '@nxsys/utils/xml';
import { downloadBankFiles } from '@nxsys/utils/bank-file-download';

const BankFileReportListing = () => {
  const { messages } = useTypedIntl();
  const navigate = useNavigateWithQuery();

  const dispatch = useDispatch<AppDispatch>();
  const { isBureauEmployer, user, isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const { bankFilesReportList } = useSelector(
    (state: AppState) => state.reportStore,
  );
  const { loading, list, pagination, queryParams } = bankFilesReportList;

  const handleFetchData = useCallback(
    (params: GetBankFilesReportParams) => {
      const newParams = {
        ...params,
        companyRefs: isBureauEmployer ? user?.companyUserIds : undefined,
      };
      dispatch(reportActions.getBankFilesReportList(newParams));
    },
    [dispatch, isBureauEmployer, user?.companyUserIds],
  );

  useEffect(() => {
    handleFetchData({
      page: 0,
      size: 20,
    });
  }, []);

  const refetch = () => {
    handleFetchData({
      ...queryParams,
    });
  };

  const handleGoBack = useCallback(() => navigate('/reports'), [navigate]);

  const renderTitle = useCallback(() => {
    return (
      <div className="flex items-center gap-x-4">
        <AppBackButton onClick={handleGoBack} />
        <AppPageTitle
          prefix={messages['report.bankFiles.title']}
          separator=""
          title={''}
        />
      </div>
    );
  }, [messages, handleGoBack]);

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleFetchData({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const downloadExcelBankFile = (data: any, bankName: string) => {
    const headers = [
      'name',
      'recipientEmail',
      'paymentReference',
      'receiverType',
      'amountCurrency',
      'amount',
      'sourceCurrency',
      'targetCurrency',
      'sortCode',
      'accountNumber',
    ];
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(workbook, worksheet, '');
    XLSX.utils.sheet_add_aoa(worksheet, [headers]);
    XLSX.writeFile(workbook, `${bankName}.xlsx`, {
      compression: true,
    });
    return;
  };

  const handleDownload = async (record: BankFilesType) => {
    const bankName = record?.paymentProvider || 'payment';
    if (record?.type === 'url') {
      window.open(record.url);
      return;
    } else if (record?.type === 'excel') {
      downloadExcelBankFile(record?.downloadData, bankName);
    } else if (record?.type === 'xml') {
      downloadXMLFile(
        record?.string || '',
        (record as { filename?: string })?.filename || `${bankName}.xml`,
      );
    } else {
      await downloadBankFiles(
        [
          { name: bankName, content: record?.string || '' },
          { name: `Ireland_${bankName}`, content: record?.secondString || '' },
        ],
        bankName,
        record?.type,
      );
    }
  };

  const [isOpenGeneratePaymentFileModal, setIsOpenGeneratePaymentFileModal] =
    useState(false);
  const [selectedRecord, setSelectedRecord] = useState<BankFilesType | null>(
    null,
  );
  const handleDownloadBankFile = (record: BankFilesType) => {
    AppConfirmation.open({
      title: 'Download Bank File',
      paragraph: messages['common.areYouSureYouWantToDownloadTheBankFile'],
      onConfirm: () => {
        setSelectedRecord(record);
        setIsOpenGeneratePaymentFileModal(true);
      },
      okText: messages['common.yes'],
      cancelText: messages['common.no'],
    });
  };
  const { run: generatePaymentFile } = useGeneratePaymentFile();
  const handleGeneratePaymentFile = async (
    values: GeneratePaymentFileValues,
  ) => {
    try {
      const provider = selectedRecord?.paymentCompany?.banksList?.find(
        (bank) => bank.id === toNumber(values.paymentProvider?.value),
      );
      const res = await generatePaymentFile(selectedRecord!.id, {
        accountName: provider?.accountName || '',
        accountNumber: provider?.accountNumber || '',
        bankName: provider?.bankName || '',
        sortCode: provider?.sortCode || '',
        paymentDate: values.paymentDate
          ? values.paymentDate.format('DD/MM/YYYY')
          : null,
      });
      if (res.success) {
        const bankName = `${provider?.bankName || 'payment'}_${values.paymentDate ? values.paymentDate.format('DD-MM-YYYY') : ''}`;
        if (res.type === 'url') {
          window.open(res.data?.string, '_blank');
        } else if (res.type === 'excel') {
          downloadExcelBankFile(res.data?.downloadData, bankName);
        } else if (res.type === 'xml') {
          downloadXMLFile(
            res.data?.string || '',
            (res.data as { filename?: string })?.filename || `${bankName}.xml`,
          );
        } else {
          await downloadBankFiles(
            [
              { name: bankName, content: res.data?.string || '' },
              {
                name: `Ireland_${bankName}`,
                content: res.data?.secondString || '',
              },
            ],
            bankName,
            res.type,
          );
        }
        showMessage({
          type: 'success',
          content: messages['common.paymentFileGeneratedSuccessfully'],
        });
        refetch();
      } else {
        showMessage({
          type: 'error',
          content: res.error || messages['common.generatePaymentFileFailed'],
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <AppsContainer fullView type="bottom" title={renderTitle()}>
      <AppCard>
        <AppsContent>
          <AppTableContainer
            rowKey="id"
            data={list ?? []}
            loading={loading}
            page={pagination.current}
            total={pagination.totalDocs}
            pageSize={pagination.pageSize}
            scroll={{ x: 1200, y: 700 }}
            titleEmpty={messages['assignment.empty']}
            columns={getColumns({
              messages,
              handleDownload,
              handleDownloadBankFile,
              isBureau,
              payrollCountry,
            })}
            onChangePagination={onChangePagination}
          />
        </AppsContent>
      </AppCard>
      {isOpenGeneratePaymentFileModal && selectedRecord && (
        <GeneratePaymentFileModal
          isOpen={isOpenGeneratePaymentFileModal}
          toggle={() => setIsOpenGeneratePaymentFileModal(false)}
          onGenerate={handleGeneratePaymentFile}
          data={selectedRecord}
        />
      )}
    </AppsContainer>
  );
};

export default BankFileReportListing;
